import { useMemo, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { AXNUIContent, AXNUIContentFooter, AXNUITheme, AXNUIThemeFooter } from "@iddataweb/idw-common-ui/types";
import { ThemingFooterValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ThemingFormContainer } from "@containers/common/theming-form-container/theming-form-container";
import { ThemingConfigurationContainer } from "@containers/common/theming-configuration-container/theming-configuration-container";
import { ThemingSubHeaderContainer } from "@containers/common/theming-sub-header-container/theming-sub-header-container";
import { ThemingFormSection } from "@containers/common/theming-form-section/theming-form-section";
import { ColorPickerFormGroup } from "@containers/common/color-picker-form-group/color-picker-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { ThemingFooterTextSection } from "./theming-footer-text-section/theming-footer-text-section";
import { ThemingFooterCustomSection } from "./theming-footer-custom-section/theming-footer-custom-section";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingFooterTabContainerProps = {
    isDisabled: boolean;
    axnuiContent: AXNUIContent,
    axnuiTheme: AXNUITheme;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    validationErrors: ThemingFooterValidationErrors;
    generatePreviewComponent: (currentTab: AXNUIThemingTabTypes) => React.ReactNode;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
    updateAXNUIThemePropertyValue: (axnuiTheme: AXNUITheme) => void;
};

export const ThemingFooterTabContainer = (props: ThemingFooterTabContainerProps) => {
    const {
        isDisabled,
        axnuiContent,
        axnuiTheme,
        inheritedAXNUIContent,
        inheritedAXNUITheme,
        pageLevel,
        validationErrors,
        generatePreviewComponent,
        updateAXNUIContentPropertyValue,
        updateAXNUIThemePropertyValue
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const updateAXNUIThemeFooterFieldValue = (key: keyof AXNUIThemeFooter, value: string | boolean) => {
        const newPropertyValue: AXNUITheme = {
            ...axnuiTheme,
            footer: {
                ...axnuiTheme.footer,
                [key]: value
            }
        };
        updateAXNUIThemePropertyValue(newPropertyValue);
    };

    const updateAXNUIContentFooterFieldValue = (key: keyof AXNUIContentFooter, value: string) => {
        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            footer: {
                ...axnuiContent.footer,
                [key]: value
            }
        };
        updateAXNUIContentPropertyValue(newPropertyValue);
    };

    const handleBackgroundColorChange = (newValue: string) => {
        updateAXNUIThemeFooterFieldValue("backgroundColor", newValue);
    };

    const handleResetBackgroundColor = () => {
        updateAXNUIThemeFooterFieldValue("backgroundColor", inheritedAXNUITheme.footer.backgroundColor);
    };

    const previewComponent = useMemo(() => {
        return generatePreviewComponent(AXNUIThemingTabTypes.FOOTER);
    }, [generatePreviewComponent]);

    return (
        <div>
            <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        {!axnuiTheme.footer.useHtmlCss && (
                            <>
                                <ThemingFormSection showSeparator={true} title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.background.title}>
                                    <ThemingFormGroupWrapper
                                        inheritedThemingPropertyValue={inheritedAXNUITheme.footer.backgroundColor}
                                        currentThemingPropertyValue={axnuiTheme.footer.backgroundColor}
                                        pageLevel={pageLevel}
                                    >
                                        <ColorPickerFormGroup
                                            controlId={"theming-footer-background-color-picker"}
                                            automationId={"theming-footer-background-color-picker"}
                                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.background.inputs.backgroundColor.label}
                                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.background.inputs.backgroundColor.label}
                                            disabled={isDisabled}
                                            isRequired={true}
                                            layout={layout}
                                            labelWidth={labelWidth}
                                            value={axnuiTheme.footer.backgroundColor || ""}
                                            onChange={handleBackgroundColorChange}
                                            position={"bottom"}
                                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.background.inputs.backgroundColor.tooltip}
                                            afterLabelIcon={isDisabled ? null : faUndo}
                                            afterLabelIconOnClick={handleResetBackgroundColor}
                                        />
                                    </ThemingFormGroupWrapper>
                                </ThemingFormSection>
                                <ThemingFormSection showSeparator={true} title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.footerText.title}>
                                    <ThemingFooterTextSection
                                        axnuiContent={axnuiContent}
                                        axnuiTheme={axnuiTheme}
                                        inheritedAXNUIContent={inheritedAXNUIContent}
                                        inheritedAXNUITheme={inheritedAXNUITheme}
                                        pageLevel={pageLevel}
                                        isDisabled={isDisabled}
                                        labelWidth={labelWidth}
                                        layout={layout}
                                        updateAXNUIContentFooterFieldValue={updateAXNUIContentFooterFieldValue}
                                        updateAXNUIThemeFooterFieldValue={updateAXNUIThemeFooterFieldValue}
                                    />
                                </ThemingFormSection>
                            </>
                        )}
                        <ThemingFormSection title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.customHtmlAndCss.title}>
                            <ThemingFooterCustomSection
                                axnuiContent={axnuiContent}
                                axnuiTheme={axnuiTheme}
                                inheritedAXNUIContent={inheritedAXNUIContent}
                                inheritedAXNUITheme={inheritedAXNUITheme}
                                pageLevel={pageLevel}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                validationErrors={validationErrors}
                                updateAXNUIContentFooterFieldValue={updateAXNUIContentFooterFieldValue}
                                updateAXNUIThemeFooterFieldValue={updateAXNUIThemeFooterFieldValue}
                            />
                        </ThemingFormSection>
                    </ThemingFormContainer>
                    {previewComponent}
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};